import * as Sentry from '@sentry/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_URL } from '@bo-utils/environmentVariables';

const paramsSerializer = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (Array.isArray(value)) {
      // Serialize arrays with the '[]' notation
      value.forEach((item) => {
        searchParams.append(`${key}[]`, String(item));
      });
    } else if (value !== undefined) {
      // Serialize other value
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${API_URL || window.backoffice.api}/`,
  credentials: 'include',
  paramsSerializer
});

export const baseQueryWithSentry: typeof baseQuery = async (
  args,
  api,
  extraOptions
) => {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: typeof args === 'string' ? args : args.url
    },
    async () => {
      const result = await baseQuery(args, api, extraOptions);

      const span = Sentry.getActiveSpan();

      if (span) {
        span.setAttributes({
          'http.status_code': result.meta?.response?.status,
          success: !result.error
        });

        const { error } = result;

        if (error) {
          const sentryError =
            !!error.data &&
            typeof error.data === 'object' &&
            'error' in error.data
              ? (error.data.error as string)
              : '';

          span.setAttributes({
            'error.type': error.status,
            'error.message': sentryError
          });

          Sentry.captureException(new Error(`API Error: ${sentryError}`), {
            tags: { source: api.endpoint },
            extra: { args }
          });
        }
      }

      return result;
    }
  );
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithSentry,
  tagTypes: [
    'AddressAutocomplete',
    'Alerts',
    'AlertNotifications',
    'AlertNotificationsCount',
    'ArenaContest',
    'Authentication',
    'Board Builder',
    'Channels',
    'ChannelUsers',
    'Correlations',
    'GameModes',
    'Games',
    'Leagues',
    'LegacyRulesets',
    'Member',
    'Members',
    'MemberChangeLogs',
    'MemberGroups',
    'MemberGroupMembers',
    'MemberGroupPaymentSettingModifiers',
    'MemberEntryCoords',
    'MemberNotes',
    'MemberPayments',
    'MemberProfile',
    'MemberReferrals',
    'MemberSignInCoords',
    'MemberTransactions',
    'NewWager',
    'NewWagers',
    'News',
    'Odds',
    'Permissions',
    'Players',
    'Polls',
    'Projections',
    'Rankings',
    'Missions',
    'MissionConditions',
    'MissionEventDefinitions',
    'MissionEventDefinitionProperties',
    'MissionEventDefinitionProperty',
    'Roles',
    'Rulesets',
    'Scores',
    'Simulation',
    'Payments',
    'PaymentMethods',
    'Polls',
    'Promotions',
    'PromotionsPendingApproval',
    'ApprovedPromotions',
    'ExpiredPromotions',
    'Unsettlements',
    'User',
    'Votes'
  ],
  endpoints: () => ({})
});
